// ─── Mock data for Encounters UI (PRD.126) ───

export type EncounterType = 'Consultation' | 'Follow-up' | 'Procedure' | 'Emergency';
export type EncounterStatus = 'Open' | 'Closed' | 'In Progress';

export interface EncounterIndicators {
  documents: number;
  notes: number;
  invoices: number;
  hasAlert?: boolean;
}

export interface Encounter {
  id: string;
  patientId: string;
  dateISO: string;
  type: EncounterType;
  provider: string;
  status: EncounterStatus;
  indicators: EncounterIndicators;
  duration: string;
  chiefComplaint: string;
  allergiesVerified: boolean;
}

// ─── Unassigned Items ───

export type UnassignedFileCategory = 'ID' | 'Document' | 'Insurance' | 'Lab' | 'Image';

export interface UnassignedFile {
  id: string;
  filename: string;
  category: UnassignedFileCategory;
  dateISO: string;
}

export interface UnassignedVital {
  id: string;
  label: string;
  source: string;
  dateISO: string;
}

export interface UnassignedItems {
  files: UnassignedFile[];
  vitals: UnassignedVital[];
}

// ─── Dummy Encounters ───

export function getMockEncounters(patientId: string): Encounter[] {
  return [
    {
      id: 'enc-001',
      patientId,
      dateISO: '2026-03-16T10:00:00',
      type: 'Consultation',
      provider: 'Dr. Sarah Chen',
      status: 'Open',
      indicators: { documents: 3, notes: 1, invoices: 1 },
      duration: '45 minutes',
      chiefComplaint:
        'Patient reports persistent headaches for the past 2 weeks, especially in the morning. Associated symptoms include light sensitivity and nausea.',
      allergiesVerified: true,
    },
    {
      id: 'enc-002',
      patientId,
      dateISO: '2026-03-10T14:00:00',
      type: 'Follow-up',
      provider: 'Dr. Sarah Chen',
      status: 'Closed',
      indicators: { documents: 1, notes: 1, invoices: 1, hasAlert: true },
      duration: '30 minutes',
      chiefComplaint:
        'Follow-up for migraine management. Patient reports improvement with prescribed medication.',
      allergiesVerified: true,
    },
    {
      id: 'enc-003',
      patientId,
      dateISO: '2026-02-20T09:30:00',
      type: 'Procedure',
      provider: 'Dr. James Wilson',
      status: 'Closed',
      indicators: { documents: 2, notes: 2, invoices: 1 },
      duration: '60 minutes',
      chiefComplaint: 'Scheduled dental cleaning and examination.',
      allergiesVerified: true,
    },
  ];
}

// ─── Dummy Unassigned Items ───

export function getMockUnassignedItems(_patientId: string): UnassignedItems {
  return {
    files: [
      {
        id: 'uf-1',
        filename: 'passport-id.jpg',
        category: 'ID',
        dateISO: '2026-03-14',
      },
      {
        id: 'uf-2',
        filename: 'birth-certificate.pdf',
        category: 'Document',
        dateISO: '2026-03-14',
      },
      {
        id: 'uf-3',
        filename: 'insurance-card.jpg',
        category: 'Insurance',
        dateISO: '2026-03-14',
      },
    ],
    vitals: [
      {
        id: 'uv-1',
        label: 'BP 120/80, HR 72',
        source: 'Device',
        dateISO: '2026-03-15',
      },
    ],
  };
}

// ─── Helpers ───

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatEncounterDate(iso: string): string {
  const d = new Date(iso);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export function formatEncounterTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

export function formatEncounterDateTime(iso: string): string {
  return `${formatEncounterDate(iso)} · ${formatEncounterTime(iso)}`;
}
